'use client';

import { ReactNode } from 'react';

import Link from 'next/link';

import { Check, Rocket, Users, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useTranslation } from '@/lib/i18n/context';
import { spaceBreaks } from '@/lib/space-breaks';

interface HelpdeskHeroProps {
  title: ReactNode;
  subtitle: string;
  ctaText?: string;
  ctaUrl?: string;
  ctaColor?: string;
  helpdeskName?: string;
}

export const HelpdeskHero = ({
  title,
  subtitle,
  ctaText,
  ctaUrl = '/demo',
  ctaColor,
  helpdeskName,
}: HelpdeskHeroProps) => {
  const { t } = useTranslation();

  const heroFeatures = [
    t.helpdesk['<1h to setup with our implementation experts'],
    t.helpdesk['#1 in AI benchmarks'],
    t.helpdesk['The deepest native integration on the market'],
  ];

  const defaultCtaText = t.helpdesk['Get started free'];
  const defaultHelpdeskName = t.helpdesk['your helpdesk'];
  return (
    <section className="bg-white pt-40 md:pt-48">
      <div className="container">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="mb-6 font-serif text-balance text-neutral-900 text-[2rem] md:text-4xl xl:text-5xl">
            {spaceBreaks(title)}
          </h1>
          <p className="mx-auto mb-8 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
            {spaceBreaks(subtitle)}
          </p>
          <div className="flex flex-col items-center gap-4">
            <Button
              size="lg"
              className={ctaColor ? '' : 'bg-black hover:bg-neutral-900'}
              style={ctaColor ? { backgroundColor: ctaColor } : undefined}
              asChild
            >
              <Link
                href={ctaUrl}
                prefetch={true}
                aria-label={
                  ctaUrl === '/demo'
                    ? t.common['Learn more about Open and book a demo']
                    : undefined
                }
              >
                {ctaText || defaultCtaText}
              </Link>
            </Button>

            {/* Features List */}
            <div className="flex flex-col items-start gap-2 pt-6 text-sm text-neutral-600 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-6 sm:gap-y-2">
              {heroFeatures.map((feature) => (
                <span key={feature} className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 shrink-0 text-green-500" />
                  {feature}
                </span>
              ))}
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground mt-2 text-sm underline transition-colors">
                  {t.helpdesk['Learn more about our onboarding']}
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                  <DialogTitle className="text-2xl">
                    {t.helpdesk['High-Touch Onboarding']}
                  </DialogTitle>
                  <DialogDescription>
                    {
                      t.helpdesk[
                        'Every customer gets white-glove implementation support'
                      ]
                    }
                  </DialogDescription>
                </DialogHeader>
                <div className="py-6">
                  {/* Timeline */}
                  <div className="relative space-y-0">
                    {/* Step 1 */}
                    <div className="relative flex gap-4 pb-8">
                      <div className="flex flex-col items-center">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black text-white">
                          <Zap className="h-5 w-5" />
                        </div>
                        <div className="h-full w-px bg-neutral-200" />
                      </div>
                      <div className="pt-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-neutral-900">
                            {t.helpdesk['Kick-off Call']}
                          </h3>
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                            {t.helpdesk['45 min']}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-neutral-600">
                          {
                            t.helpdesk[
                              'We assess your AI readiness and connect Open with'
                            ]
                          }{' '}
                          {helpdeskName || defaultHelpdeskName}
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative flex gap-4 pb-8">
                      <div className="flex flex-col items-center">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black text-white">
                          <Users className="h-5 w-5" />
                        </div>
                        <div className="h-full w-px bg-neutral-200" />
                      </div>
                      <div className="pt-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-neutral-900">
                            {t.helpdesk['Training Step']}
                          </h3>
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                            {t.helpdesk['2 weeks']}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-neutral-600">
                          {
                            t.helpdesk[
                              'We train the AI on your knowledge base, workflows, and brand voice'
                            ]
                          }
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black text-white">
                          <Rocket className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="pt-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-neutral-900">
                            {t.helpdesk['Managed Go-Live']}
                          </h3>
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                            {t.helpdesk['2 weeks']}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-neutral-600">
                          {
                            t.helpdesk[
                              'We monitor performance and fine-tune until you hit your automation targets'
                            ]
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dedicated Team */}
                  <div className="mt-8 rounded-xl bg-neutral-50 p-4">
                    <div className="flex items-start gap-3">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                      <p className="text-sm text-neutral-700">
                        <strong>{t.helpdesk['Dedicated team']}:</strong>{' '}
                        {
                          t.helpdesk[
                            'You get a dedicated engineer and a dedicated success specialist. Every onboarding is high-touch.'
                          ]
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">{t.common['Close']}</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};
